import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Text,
} from "@chakra-ui/react";
import { FiMessageSquare } from "react-icons/fi";

import { RichTextEditor } from "newui/components/ui/RichTextEditor/RichTextEditor";
import { getQuoteComment, saveQuoteComment } from "../../api";

export interface QuoteCommentProps {
	initialComment?: string;
	inviteId: number;
	isReadOnly: boolean;
	quoteId: number;
}

const DEBOUNCE_MS = 1000;

const QuoteComment: React.FC<QuoteCommentProps> = ({
	initialComment,
	inviteId,
	isReadOnly,
	quoteId,
}) => {
	const [comment, setComment] = useState(initialComment ?? "");
	const [isSaving, setIsSaving] = useState(false);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isInitialMount = useRef(true);

	// Fetch comment from API only when caller didn't provide initialComment at all
	useEffect(() => {
		if (initialComment !== undefined) {
			return;
		}
		const fetchComment = async () => {
			try {
				const result = await getQuoteComment({ inviteId, quoteId });
				if (result?.comment) {
					setComment(result.comment);
				}
			} catch {
				// Error handled by global interceptor
			}
		};
		fetchComment();
	}, [initialComment, inviteId, quoteId]);

	const hasComment =
		comment &&
		comment.replace(/<[^>]*>/g, "").trim().length > 0;

	const handleSave = useCallback(
		async (value: string) => {
			setIsSaving(true);
			try {
				await saveQuoteComment(
					{ inviteId, quoteId },
					{ comment: value }
				);
			} catch {
				// Error handled by global interceptor
			} finally {
				setIsSaving(false);
			}
		},
		[inviteId, quoteId]
	);

	const handleChange = useCallback(
		(value: string) => {
			setComment(value);

			if (isInitialMount.current) {
				isInitialMount.current = false;
				return;
			}

			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			debounceTimerRef.current = setTimeout(() => {
				handleSave(value);
			}, DEBOUNCE_MS);
		},
		[handleSave]
	);

	// Cleanup debounce timer on unmount
	useEffect(() => {
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	const sanitizedComment = useMemo(
		() => DOMPurify.sanitize(comment),
		[comment]
	);

	const defaultIndex = hasComment ? [0] : [];

	if (isReadOnly && !hasComment) {
		return null;
	}

	if (isReadOnly) {
		return (
			<div className="mt-4">
				<Text className="mb-2 font-roboto text-sm font-medium text-gray-700">
					Supplier Comment:
				</Text>
				<div
					className="prose prose-sm max-w-none rounded-md border border-gray-200 bg-gray-50 p-4 font-roboto"
					dangerouslySetInnerHTML={{ __html: sanitizedComment }}
					data-testid="quote-comment-readonly"
				/>
			</div>
		);
	}

	return (
		<div className="mt-4">
			<Accordion
				allowToggle
				defaultIndex={defaultIndex}
			>
				<AccordionItem className="!border-y-0">
					<AccordionButton className="w-fit rounded-md border border-gray-300 bg-white px-4 py-2 hover:!bg-gray-50">
						<FiMessageSquare className="mr-2 text-gray-500" />
						<Box
							as="span"
							flex="1"
							textAlign="left"
						>
							<Text className="font-roboto text-sm font-medium text-gray-700">
								{hasComment ? "Comment" : "Add Comment"}
							</Text>
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel className="!px-0 !pt-3 !pb-0">
						<RichTextEditor
							value={comment}
							onChange={handleChange}
							placeholder="Enter your comment here..."
							disabled={isReadOnly}
							guideLine="Add notes about your pricing, assumptions, or exclusions."
							testId="quote-comment-editor"
							noMargin
						/>
						{isSaving && (
							<Text
								className="mt-1 font-roboto text-xs text-blue-400"
								data-testid="quote-comment-saving"
								role="status"
							>
								Saving...
							</Text>
						)}
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
		</div>
	);
};

export { QuoteComment };
